// src/components/Experiment/AdjustmentsDialog.js
import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Modal,
  Button,
  Form,
  Row,
  Col,
  Alert,
  Spinner,
  Tabs,
  Tab,
  Card,
  Badge,
  OverlayTrigger,
  Tooltip
} from 'react-bootstrap';
import './AdjustmentsDialog.css';

const AdjustmentsDialog = ({
  show,
  onHide,
  experiment,
  adjustmentsTables,
  adjustmentsLoading,
  onLoadTables,
  onCreateTable,
  onUpdateTable,
  onDeleteTable,
  onUploadExcel,
  onApplyAdjustments,
  adjustmentsError,
  adjustmentsSuccess// Add this prop
}) => {
  const [activeTab, setActiveTab] = useState('tables');
  const [tableName, setTableName] = useState('');
  const [coefficients, setCoefficients] = useState([]);
  const [excelFile, setExcelFile] = useState(null);
  const [selectedTableId, setSelectedTableId] = useState('');
  const [selectedColumns, setSelectedColumns] = useState([]);
  const [localError, setLocalError] = useState(null);
  const [localSuccess, setLocalSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [applying, setApplying] = useState(false);
  const [isNewTable, setIsNewTable] = useState(true);
  const [deletingTableId, setDeletingTableId] = useState(null);

  const fileInputRef = useRef(null);

  // Get plate dimensions from experiment
  const plateSchema = experiment?.plateSchema;
  const rows = plateSchema?.rows || 0;
  const cols = plateSchema?.cols || 0;

  // Load tables when dialog opens
  useEffect(() => {
    if (show) {
      onLoadTables();
      setActiveTab('tables');
      setTableName('');
      setSelectedTableId('');
      setSelectedColumns([]);
      setIsNewTable(true);
      setLocalError(null);
      setLocalSuccess(null);
      initializeCoefficients();
    }
  }, [show]);

  // Initialize coefficients when rows/cols change
  useEffect(() => {
    if (show && rows > 0 && cols > 0) {
      initializeCoefficients();
    }
  }, [show, rows, cols]);

  // Update coefficients when a table is selected
  useEffect(() => {
    if (selectedTableId && adjustmentsTables.length > 0) {
      const selectedTable = adjustmentsTables.find(table => table.id === selectedTableId);
      if (selectedTable) {
        setTableName(selectedTable.name);
        setCoefficients(selectedTable.table?.coefficients || []);
        setIsNewTable(false);
      }
    } else if (!selectedTableId) {
      setIsNewTable(true);
      setTableName('');
      initializeCoefficients();
    }
  }, [selectedTableId, adjustmentsTables]);

  // Propagate external errors/success to local state
  useEffect(() => {
    if (adjustmentsError) {
      setLocalError(adjustmentsError);
    }
    if (adjustmentsSuccess) {
      setLocalSuccess(adjustmentsSuccess);
    }
  }, [adjustmentsError, adjustmentsSuccess]);

  const initializeCoefficients = () => {
    const newCoefficients = [];
    for (let i = 0; i < rows; i++) {
      const row = [];
      for (let j = 0; j < cols; j++) {
        row.push(1.0); // Default coefficient = 1.0 (no adjustment)
      }
      newCoefficients.push(row);
    }
    setCoefficients(newCoefficients);
  };

  const handleCoefficientChange = (rowIndex, colIndex, value) => {
    const newCoefficients = [...coefficients];
    const numValue = parseFloat(value);

    if (!isNaN(numValue)) {
      newCoefficients[rowIndex][colIndex] = numValue;
      setCoefficients(newCoefficients);
    }
  };

  const handleBulkUpdate = (value) => {
    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    const newCoefficients = coefficients.map(row =>
      row.map(() => numValue)
    );
    setCoefficients(newCoefficients);
  };

  const handleResetToDefaults = () => {
    initializeCoefficients();
  };

  const handleExcelUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Check file type
    const validTypes = [
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'text/csv'
    ];

    if (!validTypes.includes(file.type) && !file.name.match(/\.(xls|xlsx|csv)$/i)) {
      setLocalError('Please upload a valid Excel or CSV file');
      return;
    }

    setExcelFile(file);
  };

  const handleProcessExcel = async () => {
    if (!excelFile) {
      setLocalError('Please select an Excel file first');
      return;
    }

    if (rows === 0 || cols === 0) {
      setLocalError('Cannot determine plate dimensions');
      return;
    }

    try {
      setSaving(true);
      setLocalError(null);
      setLocalSuccess('Uploading and processing Excel file...');

      const formData = new FormData();
      formData.append('file', excelFile);

      // Use filename as table name if no name provided and it's a new table
      const finalTableName = tableName || (isNewTable ? excelFile.name.replace(/\.[^/.]+$/, "") : tableName);
      if (finalTableName) {
        formData.append('name', finalTableName);
      }

      const result = await onUploadExcel(formData);

      if (result) {
        setLocalSuccess('Adjustments table created from Excel successfully');

        // If result contains table data, update the form
        if (result.table?.coefficients && result.table.coefficients.length > 0) {
          setCoefficients(result.table.coefficients);
        }

        // Update table name if it was auto-generated
        if (result.name && !tableName) {
          setTableName(result.name);
        }

        // Clear file input
        if (fileInputRef.current) {
          fileInputRef.current.value = '';
        }
        setExcelFile(null);

        // Refresh tables list
        onLoadTables();

        // Auto-select the newly created table
        if (result.id) {
          setSelectedTableId(result.id);
          setIsNewTable(false);
        }

        return result;
      }
    } catch (error) {
      console.error('Error processing Excel file:', error);
      setLocalError(error.message || 'Failed to process Excel file');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const handleSaveTable = async () => {
    if (!tableName.trim()) {
      setLocalError('Please enter a name for the adjustments table');
      return;
    }

    if (coefficients.length !== rows || coefficients[0]?.length !== cols) {
      setLocalError(`Table dimensions must be ${rows}x${cols} to match plate schema`);
      return;
    }

    try {
      setSaving(true);
      setLocalError(null);
      setLocalSuccess(null);

      const tableData = {
        name: tableName,
        rows,
        cols,
        table: {
          coefficients
        }
      };

      let result;

      if (isNewTable) {
        // Create new table
        result = await onCreateTable(tableData);
        if (result) {
          setLocalSuccess('Adjustments table created successfully');
          setSelectedTableId(result.id);
          setIsNewTable(false);
          onLoadTables();
        }
      } else {
        // Update existing table
        result = await onUpdateTable(selectedTableId, tableData);
        if (result) {
          setLocalSuccess('Adjustments table updated successfully');
          onLoadTables();
        }
      }

    } catch (error) {
      console.error('Error saving adjustments table:', error);
      setLocalError(error.message || `Failed to ${isNewTable ? 'create' : 'update'} adjustments table`);
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTable = async (tableId, tableName) => {

    try {
      setDeletingTableId(tableId);
      setLocalError(null);
      setLocalSuccess(null);

      await onDeleteTable(tableId);

      setLocalSuccess(`Table "${tableName}" deleted successfully`);

      // If the deleted table was selected, clear the selection
      if (selectedTableId === tableId) {
        setSelectedTableId('');
        setIsNewTable(true);
        setTableName('');
        initializeCoefficients();
      }

      // Refresh tables list
      onLoadTables();

    } catch (error) {
      console.error('Error deleting table:', error);
      setLocalError(error.message || 'Failed to delete table');
    } finally {
      setDeletingTableId(null);
    }
  };

  const handleApplyAdjustments = async () => {
    if (!selectedTableId) {
      setLocalError('Please select or create an adjustments table');
      return;
    }

    if (selectedColumns.length === 0) {
      setLocalError('Please select at least one column to adjust');
      return;
    }

    try {
      setApplying(true);
      setLocalError(null);
      setLocalSuccess(null);

      await onApplyAdjustments(selectedTableId, selectedColumns);

      // Close dialog after successful application
      onHide();

    } catch (error) {
      console.error('Error applying adjustments:', error);
      setLocalError(error.message || 'Failed to apply adjustments');
    } finally {
      setApplying(false);
    }
  };

  const handleSelectTable = (tableId) => {
    setSelectedTableId(tableId);
    setIsNewTable(false);
  };

  const handleCreateNewTable = () => {
    setSelectedTableId('');
    setTableName('');
    initializeCoefficients();
    setIsNewTable(true);
  };

  // Get column options from experiment data with proper ordering
  const getOrderedColumnOptions = useMemo(() => {
    if (!experiment?.experimentData?.columns_metadata) return [];

    const columns_metadata = experiment.experimentData.columns_metadata;
    const dataCols = [];

    Object.entries(columns_metadata).forEach(([colName, metadata]) => {
      // Only include data columns (not sample info or class columns)
      if (metadata.type === 'data') {
        dataCols.push({
          value: colName,
          label: colName,
          job: metadata.filename,
          channel: metadata.channel,
          type: metadata.calculate_type,
          metadata: metadata
        });
      }
    });

    // Sort data columns by job chronology using job_order field
    dataCols.sort((a, b) => {
      const metaA = a.metadata;
      const metaB = b.metadata;

      const orderA = metaA.job_order !== undefined ? metaA.job_order : metaA.job_id;
      const orderB = metaB.job_order !== undefined ? metaB.job_order : metaB.job_id;

      if (orderA !== orderB) {
        const result = orderA - orderB;
        return result;
      }

      const channelOrder = {
        'R': 0,
        'G': 1,
        'B': 2,
        'intensity': 3,
        'rg_intensity': 4,
        'rb_intensity': 5,
        'gb_intensity': 6
      };
      const channelA = channelOrder[metaA.channel] !== undefined ? channelOrder[metaA.channel] : 99;
      const channelB = channelOrder[metaB.channel] !== undefined ? channelOrder[metaB.channel] : 99;
      const channelResult = channelA - channelB;

      return channelResult;
    });

    return dataCols;
  }, [experiment?.experimentData?.columns_metadata]);

  const columnOptions = getOrderedColumnOptions;

  // Group columns by job
  const groupedColumns = useMemo(() => {
    return columnOptions.reduce((groups, column) => {
      const job = column.job || 'Unknown';
      if (!groups[job]) {
        groups[job] = [];
      }
      groups[job].push(column);
      return groups;
    }, {});
  }, [columnOptions]);

  // Helper functions for column selection
  const selectAllColumns = () => {
    setSelectedColumns(columnOptions.map(col => col.value));
  };

  const selectAllRGBColumns = () => {
    const rgbColumns = columnOptions
      .filter(col => ['R', 'G', 'B'].includes(col.channel))
      .map(col => col.value);
    setSelectedColumns(rgbColumns);
  };

  const selectAllIntensityColumns = () => {
    const intensityColumns = columnOptions
      .filter(col => ['intensity', 'rg_intensity', 'rb_intensity', 'gb_intensity'].includes(col.channel))
      .map(col => col.value);
    setSelectedColumns(intensityColumns);
  };

  const clearAllColumns = () => {
    setSelectedColumns([]);
  };

  // Format date for display
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Handle tab change
  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setLocalError(null);
    setLocalSuccess(null);
  };

  return (
    <Modal
      show={show}
      onHide={onHide}
      size="xl"
      centered
      backdrop="static"
      className="adjustments-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>
          <i className="bi bi-sliders me-2"></i>
          Plate Adjustments
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {/* Excel Upload Section */}
        <div className="excel-upload-section mb-4">
          <h6 className="mb-3">
            <i className="bi bi-file-earmark-excel text-success me-2"></i>
            Upload Coefficients from Excel/CSV
          </h6>
          <Alert variant="light" className="mb-3">
            <small>
              Upload an Excel/CSV file with coefficients. The file should contain {rows}x{cols} numeric values.
              Use comma (,) or dot (.) as decimal separator.
            </small>
          </Alert>

          <Row className="align-items-center">
            <Col md={8}>
              <Form.Group>
                <Form.Control
                  type="file"
                  accept=".xls,.xlsx,.csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                  onChange={handleExcelUpload}
                  ref={fileInputRef}
                  disabled={saving}
                />
                <Form.Text className="text-muted">
                  {excelFile ? `Selected: ${excelFile.name}` : 'Select Excel or CSV file'}
                </Form.Text>
              </Form.Group>
            </Col>
            <Col md={4}>
              <Button
                variant="success"
                onClick={handleProcessExcel}
                disabled={!excelFile || saving}
                className="w-100"
              >
                {saving ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    Processing...
                  </>
                ) : (
                  <>
                    <i className="bi bi-upload me-2"></i>
                    Upload & Load
                  </>
                )}
              </Button>
            </Col>
          </Row>
        </div>

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => handleTabChange(k)}
          className="mb-3"
          fill
        >
          {/* Tables Tab */}
          <Tab eventKey="tables" title="Adjustments Tables">
            <div className="adjustments-tab-content">
              <Row>
                <Col md={12}>
                  {/* Tables List */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6>Existing Tables</h6>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleCreateNewTable}
                      >
                        <i className="bi bi-plus-circle me-1"></i>
                        Create New
                      </Button>
                    </div>

                    {adjustmentsLoading ? (
                      <div className="text-center py-4">
                        <Spinner animation="border" />
                        <p className="mt-2">Loading tables...</p>
                      </div>
                    ) : adjustmentsTables.length === 0 ? (
                      <Alert variant="info">
                        <i className="bi bi-info-circle me-2"></i>
                        No adjustments tables found. Create one or upload from Excel.
                      </Alert>
                    ) : (
                      <div className="tables-grid">
                        {adjustmentsTables.map(table => (
                          <Card
                            key={table.id}
                            className={`mb-2 table-card ${selectedTableId === table.id ? 'table-card-selected' : ''}`}
                            onClick={() => handleSelectTable(table.id)}
                          >
                            <Card.Body className="p-3">
                              <div className="d-flex justify-content-between align-items-start">
                                <div>
                                  <h6 className="mb-1">{table.name}</h6>
                                  <div className="text-muted small">
                                    <div>
                                      <i className="bi bi-grid me-1"></i>
                                      {table.rows}x{table.cols}
                                    </div>
                                    <div>
                                      <i className="bi bi-calendar me-1"></i>
                                      Created: {formatDate(table.created_at)}
                                    </div>
                                    {table.updated_at !== table.created_at && (
                                      <div>
                                        <i className="bi bi-arrow-clockwise me-1"></i>
                                        Updated: {formatDate(table.updated_at)}
                                      </div>
                                    )}
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <Badge bg={table.status === 'completed' ? 'success' : 'warning'}>
                                    {table.status}
                                  </Badge>
                                  <Button
                                    variant="outline-danger"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteTable(table.id, table.name);
                                    }}
                                    disabled={deletingTableId === table.id}
                                  >
                                    {deletingTableId === table.id ? (
                                      <Spinner animation="border" size="sm" />
                                    ) : (
                                      <i className="bi bi-trash"></i>
                                    )}
                                  </Button>
                                </div>
                              </div>
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Table Editor */}
                  <div className="table-editor">
                    <h6 className="mb-3">
                      {isNewTable ? 'Create New Table' : 'Edit Selected Table'}
                    </h6>

                    {/* First Row: Form controls */}
                    <Row className="align-items-end mb-4">
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Table Name *</Form.Label>
                          <Form.Control
                            type="text"
                            value={tableName}
                            onChange={(e) => setTableName(e.target.value)}
                            placeholder="e.g., Camera Correction v1"
                            disabled={saving}
                            className="table-name-input"
                          />
                        </Form.Group>
                      </Col>
                      <Col md={4}>
                        <Form.Group>
                          <Form.Label>Bulk Update All Cells</Form.Label>
                          <div className="d-flex gap-2">
                            <Form.Control
                              type="number"
                              step="0.01"
                              placeholder="Enter value"
                              onChange={(e) => handleBulkUpdate(e.target.value)}
                              className="bulk-update-input"
                            />
                            <Button
                              variant="outline-secondary"
                              onClick={handleResetToDefaults}
                              className="reset-button"
                            >
                              Reset to 1.0
                            </Button>
                          </div>
                        </Form.Group>
                      </Col>
                      <Col md={4} className="d-flex justify-content-end">
                        <Button
                          variant="primary"
                          onClick={handleSaveTable}
                          disabled={!tableName.trim() || saving || coefficients.length === 0}
                          className="save-button"
                        >
                          {saving ? (
                            <>
                              <Spinner animation="border" size="sm" className="me-2" />
                              Saving...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-save me-2"></i>
                              {isNewTable ? 'Create Table' : 'Update Table'}
                            </>
                          )}
                        </Button>
                      </Col>
                    </Row>

                    {/* Second Row: Coefficients Table */}
                    <Row>
                      <Col md={12}>
                        <div className="coefficients-table-container">
                          <div className="table-wrapper">
                            <table className="coefficients-table">
                              <thead>
                                <tr>
                                  <th></th>
                                  {Array.from({ length: cols }, (_, i) => (
                                    <th key={i} className="text-center">
                                      {i + 1}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {coefficients.map((row, rowIndex) => (
                                  <tr key={rowIndex}>
                                    <th className="row-header">{String.fromCharCode(65 + rowIndex)}</th>
                                    {row.map((coefficient, colIndex) => (
                                      <td key={colIndex}>
                                        <Form.Control
                                          type="number"
                                          step="0.01"
                                          value={coefficient}
                                          onChange={(e) =>
                                            handleCoefficientChange(rowIndex, colIndex, e.target.value)
                                          }
                                          className="coefficient-input"
                                          size="sm"
                                        />
                                      </td>
                                    ))}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </div>
                </Col>
              </Row>
            </div>
          </Tab>

          {/* Columns Tab */}
          <Tab eventKey="columns" title="Select Columns">
            <div className="adjustments-tab-content">
              <Alert variant="info" className="mb-4">
                <i className="bi bi-info-circle me-2"></i>
                Select columns to apply adjustments to. Columns are ordered chronologically by job.
              </Alert>

              <Row>
                <Col md={6}>
                  <Card className="mb-4">
                    <Card.Header>
                      <h6 className="mb-0">Selected Adjustments Table</h6>
                    </Card.Header>
                    <Card.Body>
                      {selectedTableId && adjustmentsTables.find(t => t.id === selectedTableId) ? (
                        <>
                          <h5>{adjustmentsTables.find(t => t.id === selectedTableId).name}</h5>
                          <div className="text-muted">
                            <div><i className="bi bi-grid me-2"></i> Dimensions: {rows}x{cols}</div>
                            <div><i className="bi bi-calendar me-2"></i> Created: {formatDate(adjustmentsTables.find(t => t.id === selectedTableId).created_at)}</div>
                          </div>
                        </>
                      ) : isNewTable ? (
                        <div className="text-center py-3">
                          <i className="bi bi-plus-circle display-4 text-muted mb-3"></i>
                          <p>New table will be created</p>
                          <p className="text-muted small">{tableName || 'Unnamed table'}</p>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <i className="bi bi-exclamation-triangle display-4 text-warning mb-3"></i>
                          <p>No table selected</p>
                          <p className="text-muted small">Go back to "Adjustments Tables" tab to select or create a table</p>
                        </div>
                      )}
                    </Card.Body>
                  </Card>
                </Col>

                <Col md={6}>
                  {/* Quick Selection Buttons */}
                  <div className="quick-selection-buttons mb-4">
                    <h6 className="mb-3">Quick Selection</h6>
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={selectAllColumns}
                      >
                        <i className="bi bi-check-all me-1"></i>
                        Select All
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={selectAllRGBColumns}
                      >
                        <i className="bi bi-palette me-1"></i>
                        All RGB
                      </Button>
                      <Button
                        variant="outline-info"
                        size="sm"
                        onClick={selectAllIntensityColumns}
                      >
                        <i className="bi bi-brightness-high me-1"></i>
                        All Intensity
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={clearAllColumns}
                      >
                        <i className="bi bi-x-circle me-1"></i>
                        Clear All
                      </Button>
                    </div>
                    <Alert variant="info" className="mt-3 mb-0">
                      <i className="bi bi-info-circle me-2"></i>
                      <strong>{selectedColumns.length}</strong> column(s) selected for adjustment
                    </Alert>
                  </div>

                  {/* Columns Selection */}
                  <div className="columns-section">
                    <h6 className="mb-3">Available Columns</h6>

                    <div className="columns-selector">
                      <div className="columns-list border rounded p-3" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        {Object.entries(groupedColumns).map(([job, columns]) => (
                          <div key={job} className="mb-4">
                            <div className="job-header border-bottom pb-2 mb-3">
                              <h6 className="mb-0 text-muted">
                                <i className="bi bi-image me-2"></i>
                                {job}
                              </h6>
                            </div>
                            <div className="row row-cols-3 g-3">
                              {columns.map(column => (
                                <div key={column.value} className="col">
                                  <Form.Check
                                    type="checkbox"
                                    id={`col-${column.value}`}
                                    label={
                                      <div className="d-flex align-items-center">
                                        <Badge
                                          bg={
                                            column.channel === 'R' ? 'danger' :
                                            column.channel === 'G' ? 'success' :
                                            column.channel === 'B' ? 'primary' :
                                            'secondary'
                                          }
                                          className="me-2"
                                        >
                                          {column.channel}
                                        </Badge>
                                        <small className="text-truncate" title={column.type || 'value'}>
                                          {column.type || 'value'}
                                        </small>
                                      </div>
                                    }
                                    checked={selectedColumns.includes(column.value)}
                                    onChange={(e) => {
                                      if (e.target.checked) {
                                        setSelectedColumns([...selectedColumns, column.value]);
                                      } else {
                                        setSelectedColumns(
                                          selectedColumns.filter(col => col !== column.value)
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </Col>
              </Row>
            </div>
          </Tab>
        </Tabs>

        {/* Error/Success Messages */}
        {localError && (
          <Alert variant="danger" className="mt-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            {localError}
          </Alert>
        )}

        {localSuccess && (
          <Alert variant="success" className="mt-3">
            <i className="bi bi-check-circle me-2"></i>
            {localSuccess}
          </Alert>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-end w-100 gap-2">
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleApplyAdjustments}
            disabled={!selectedTableId || selectedColumns.length === 0 || applying}
          >
            {applying ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Applying...
              </>
            ) : (
              'Apply Adjustments'
            )}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default AdjustmentsDialog;